import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem as MuiMenuItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useGetSchedulerHomeDataQuery,
  useGetSchedulerReferenceDataQuery,
  useGetScheduleExecHistoryQuery,
  useChangeScheduleStatusMutation,
  useDeleteScheduleMutation,
  useGetAccountsQuery,
  useLazyGetScheduleDetailsQuery,
} from "../../redux/api";
import ScheduleEditorFullScreen from "./ScheduleEditorFullScreen";

export default function SchedulerPage() {
  const defaultFilters = {
    workflow_group: "ALL",
    assigned_workflow: "",
    status: "ALL",
    schedule_name: "",
  };
  const { t } = useTranslation();
  const { account: baseAccount, user } = useSelector((state) => state.queryParams);
  const [filters, setFilters] = useState(defaultFilters);
  const [editing, setEditing] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [workflowTarget, setWorkflowTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, row: null });
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowItems, setWorkflowItems] = useState([]);
  const [rowScheduleDetails, setRowScheduleDetails] = useState({});
  const prevEffectiveAccountRef = useRef(null);
  const [historyDrawerWidth, setHistoryDrawerWidth] = useState(1000);
  const historyResizeRef = useRef({ startX: 0, startWidth: 1000 });

  const [selectedAccount, setSelectedAccount] = useState(
    baseAccount && baseAccount.toUpperCase() !== "ALL" ? baseAccount : ""
  );
  const effectiveAccount = baseAccount && baseAccount.toUpperCase() === "ALL" ? selectedAccount : baseAccount;

  const accountsQuery = useGetAccountsQuery(undefined, {
    skip: !baseAccount || baseAccount.toUpperCase() !== "ALL",
  });
  const accounts = useMemo(() => {
    const payload = accountsQuery.data;
    const ok = payload?.current_state === "SUCCESS" || payload?.status === "SUCCESS";
    if (!ok) return [];

    const collectStrings = (value) => {
      if (Array.isArray(value)) {
        return value
          .map((v) => {
            if (typeof v === "string") return v.trim();
            if (v && typeof v === "object") return String(v.name || v.account || "").trim();
            return "";
          })
          .filter(Boolean);
      }
      if (typeof value === "string" && value.trim()) return [value.trim()];
      return [];
    };

    // Prefer explicit "accounts" key only; avoid unrelated fields like "user".
    const candidates = [
      ...(collectStrings(payload?.data?.accounts)),
      ...(collectStrings(payload?.accounts)),
    ];
    return Array.from(new Set(candidates)).sort((a, b) => a.localeCompare(b));
  }, [accountsQuery.data]);

  const home = useGetSchedulerHomeDataQuery({
    user,
    account: effectiveAccount,
    workflow_group: filters.workflow_group,
    assigned_workflow: filters.assigned_workflow,
    status: filters.status,
    schedule_name: filters.schedule_name,
  }, { skip: !effectiveAccount });
  const ref = useGetSchedulerReferenceDataQuery({ user, account: effectiveAccount }, { skip: !effectiveAccount });
  const history = useGetScheduleExecHistoryQuery(
    historyTarget
      ? {
          account: effectiveAccount,
          user,
          workflow_group: historyTarget.workflow_group,
          schedule_name: historyTarget.schedule_name,
          row_count: 50,
        }
      : { account: "", workflow_group: "", schedule_name: "" },
    { skip: !historyTarget || !effectiveAccount }
  );
  const [changeStatus, changeStatusState] = useChangeScheduleStatusMutation();
  const [deleteSchedule, deleteState] = useDeleteScheduleMutation();
  const [loadScheduleDetails] = useLazyGetScheduleDetailsQuery();

  const homeState = home?.data?.current_state || home?.data?.status;
  const refState = ref?.data?.current_state || ref?.data?.status;
  const rows = effectiveAccount && homeState === "SUCCESS" ? home?.data?.data?.schedules || [] : [];
  const workflowGroups = effectiveAccount && refState === "SUCCESS" ? ref?.data?.data?.workflow_groups || [] : [];
  const assignableWorkflows = effectiveAccount && refState === "SUCCESS" ? ref?.data?.data?.assignable_workflows || [] : [];
  const calendarNames = effectiveAccount && refState === "SUCCESS" ? ref?.data?.data?.calendars || [] : [];

  const historyState = history?.data?.current_state || history?.data?.status;
  const historyRows = historyState === "SUCCESS" ? history?.data?.data || [] : [];

  const toList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string" && value.trim()) return value.split(",").map((v) => v.trim()).filter(Boolean);
    return [];
  };

  const toText = (value, fallback = "-") => {
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
    return fallback;
  };
  const formatHistoryDate = (value) => {
    const raw = toText(value, "");
    if (!raw) return "-";
    const ts = Number(raw);
    if (!Number.isNaN(ts) && ts > 0) {
      try {
        return new Date(ts).toLocaleString();
      } catch (e) {
        return raw;
      }
    }
    return raw;
  };
  const formatHistoryStatus = (value) => {
    const raw = toText(value, "");
    if (!raw) return "-";
    try {
      return decodeURIComponent(raw.replace(/\+/g, "%20"));
    } catch (e) {
      return raw.replace(/\+/g, " ");
    }
  };
  const formatHistoryRecurrence = (value) => {
    let raw = toText(value, "");
    if (!raw) return "-";

    // Normalize escaped payloads like: "1 \u0002INTERVAL_MONTH\u0003"
    raw = raw.replace(/^"(.*)"$/, "$1");
    if (/\\u[0-9a-fA-F]{4}/.test(raw)) {
      try {
        raw = JSON.parse(`"${raw.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`);
      } catch (e) {
        // keep original raw if decode fails
      }
    }
    // Remove STX/ETX wrappers and any remaining control chars
    raw = raw.replace(/\u0002/g, "").replace(/\u0003/g, "").replace(/[\u0000-\u001F]/g, "").trim();

    const localized = t(raw);
    if (localized && localized !== raw) return localized;
    const intervalFallbackKeys = {
      INTERVAL_SECOND: "SCHED_RECUR_LBL_SEC",
      INTERVAL_MINUTE: "SCHED_RECUR_LBL_MIN",
      INTERVAL_HOUR: "SCHED_RECUR_LBL_HR",
      INTERVAL_DAY: "SCHED_RECUR_LBL_DAY",
      INTERVAL_WEEK: "SCHED_RECUR_LBL_WK",
      INTERVAL_MONTH: "SCHED_RECUR_LBL_MNTH",
      INTERVAL_YEAR: "SCHED_RECUR_LBL_YR",
    };
    // Try token-level localization for mixed values like "1 INTERVAL_MONTH"
    const tokenLocalized = raw
      .split(/\s+/)
      .map((token) => {
        const tk = t(token);
        if (tk && tk !== token) return tk;
        const altKey = intervalFallbackKeys[token];
        if (altKey) {
          const alt = t(altKey);
          if (alt && alt !== altKey) return alt;
        }
        return token;
      })
      .join(" ");
    return tokenLocalized;
  };
  const allWorkflowOptions = useMemo(() => {
    if (!effectiveAccount) return [];
    const fromRows = rows.flatMap((r) => toList(r.workflows || r.assigned_workflow || r.assignedWorkflows || r.WORKFLOW_KEY));
    return Array.from(new Set([...(assignableWorkflows || []), ...fromRows].filter(Boolean))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [assignableWorkflows, rows, effectiveAccount]);
  const toDisplayLabel = (value) => {
    const raw = toText(value, "");
    if (!raw) return "-";
    const localized = t(raw);
    return localized && localized !== raw ? localized : raw;
  };
  const groupAssignedWorkflows = (workflowList) => {
    const grouped = {};
    toList(workflowList).forEach((wf) => {
      const [group, ...rest] = String(wf).split("/");
      const name = rest.join("/");
      if (!group || !name) return;
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(name);
    });
    Object.keys(grouped).forEach((g) => grouped[g].sort((a, b) => a.localeCompare(b)));
    return grouped;
  };

  const onStatusToggle = async (row) => {
    const mode = row.status === "enabled" ? "DISABLE" : "ENABLE";
    await changeStatus({
      user,
      account: effectiveAccount,
      schedule_name: row.schedule_name,
      schedule_key: row.schedule_key,
      mode,
      request_ip: "react-workbench",
    });
    home.refetch();
  };

  const onDelete = async () => {
    if (!pendingDelete) return;
    await deleteSchedule({
      user,
      account: effectiveAccount,
      workflow_group_name: pendingDelete.workflow_group,
      schedule_name: pendingDelete.schedule_name,
      is_userspace: true,
      is_server: true,
      request_ip: "react-workbench",
      ext: "json",
    });
    setPendingDelete(null);
    home.refetch();
  };
  const openActionMenu = (event, row) => setActionMenu({ anchorEl: event.currentTarget, row });
  const closeActionMenu = () => setActionMenu({ anchorEl: null, row: null });
  const openWorkflowSlider = async (row) => {
    setWorkflowTarget(row);
    setWorkflowLoading(true);
    setWorkflowItems([]);
    const res = await loadScheduleDetails({
      user,
      account: effectiveAccount,
      workflow_group_name: row.workflow_group,
      schedule_name: row.schedule_name,
    });
    const schedule = res?.data?.data?.SCHEDULE_DEFINITION?.SCHEDULE || res?.data?.SCHEDULE_DEFINITION?.SCHEDULE;
    const wfKeys = Array.isArray(schedule?.WORKFLOW_KEY)
      ? schedule.WORKFLOW_KEY.map((w) => `${w?.WF_GROUP || ""}/${w?.WF_NAME || ""}`).filter((x) => x !== "/")
      : [];
    setWorkflowItems(wfKeys);
    setWorkflowLoading(false);
  };

  useEffect(() => {
    if (prevEffectiveAccountRef.current !== null && prevEffectiveAccountRef.current !== effectiveAccount) {
      setRowScheduleDetails({});
    }
    prevEffectiveAccountRef.current = effectiveAccount;
  }, [effectiveAccount]);

  useEffect(() => {
    const missingRows = rows.filter((r) => !rowScheduleDetails[`${r.workflow_group}::${r.schedule_name}`]);
    if (!missingRows.length) return;
    missingRows.forEach((r) => {
      loadScheduleDetails({
        user,
        account: effectiveAccount,
        workflow_group_name: r.workflow_group,
        schedule_name: r.schedule_name,
      }).then((res) => {
        const schedule = res?.data?.data?.SCHEDULE_DEFINITION?.SCHEDULE || res?.data?.SCHEDULE_DEFINITION?.SCHEDULE || null;
        if (!schedule) return;
        const key = `${r.workflow_group}::${r.schedule_name}`;
        setRowScheduleDetails((prev) => ({ ...prev, [key]: schedule }));
      });
    });
  }, [rows, effectiveAccount, user, loadScheduleDetails, rowScheduleDetails]);

  const apiMessage = home?.data?.message || ref?.data?.message || accountsQuery?.error?.data?.message;
  const apiFailed =
    (home?.data && homeState !== "SUCCESS") ||
    (ref?.data && refState !== "SUCCESS") ||
    accountsQuery.isError;
  const errorText =
    home.error?.data?.message ||
    home.error?.error ||
    ref.error?.data?.message ||
    ref.error?.error ||
    accountsQuery.error?.data?.message ||
    accountsQuery.error?.error;
  const isGridLoading = home.isLoading || home.isFetching || changeStatusState.isLoading || deleteState.isLoading;
  const hasActiveFilters =
    filters.workflow_group !== defaultFilters.workflow_group ||
    filters.assigned_workflow !== defaultFilters.assigned_workflow ||
    filters.status !== defaultFilters.status ||
    filters.schedule_name !== defaultFilters.schedule_name ||
    (baseAccount && baseAccount.toUpperCase() === "ALL" && !!selectedAccount);
  const onClearFilters = () => {
    setFilters(defaultFilters);
    if (baseAccount && baseAccount.toUpperCase() === "ALL") {
      setSelectedAccount("");
    }
  };
  const startHistoryResize = (e) => {
    e.preventDefault();
    historyResizeRef.current = {
      startX: e.clientX,
      startWidth: historyDrawerWidth,
    };
    const onMouseMove = (moveEvt) => {
      const dx = moveEvt.clientX - historyResizeRef.current.startX;
      const next = historyResizeRef.current.startWidth + dx;
      const minWidth = 760;
      const maxWidth = window.innerWidth - 40;
      setHistoryDrawerWidth(Math.max(minWidth, Math.min(maxWidth, next)));
    };
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };
  const handleEditorSaved = async () => {
    setEditing(null);
    // Backend save can be eventually consistent; do a follow-up refresh.
    await home.refetch();
    setTimeout(() => {
      home.refetch();
    }, 1200);
  };
  const gridCols = "120px repeat(8, minmax(140px, 1fr))";

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Scheduler Home</Typography>
        <Button
          variant="contained"
          disabled={!effectiveAccount}
          onClick={() => setEditing({ isNew: true })}
        >
          New Schedule
        </Button>
      </Stack>

      {!effectiveAccount ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {baseAccount && baseAccount.toUpperCase() === "ALL"
            ? "Account is ALL. Please select an account from the Filters section."
            : "Account is missing in URL/query params."}
        </Alert>
      ) : null}

      <Box
        sx={{
          mb: 2,
          border: "1px solid #d5d9e0",
          borderRadius: 2,
          bgcolor: "#f8f9fc",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #e1e5ec", bgcolor: "#eef1f6" }}>
          <Typography sx={{ fontWeight: 700, color: "#5f6878", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: 0.3 }}>
            Filters
          </Typography>
        </Box>
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap" }}>
            {baseAccount && baseAccount.toUpperCase() === "ALL" ? (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Account</InputLabel>
                <Select
                  label="Account"
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 320 },
                    },
                  }}
                >
                  <MenuItem value="">Select...</MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc} value={acc}>
                      {acc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Workflow Group</InputLabel>
              <Select
                label="Workflow Group"
                value={filters.workflow_group}
                onChange={(e) => setFilters((f) => ({ ...f, workflow_group: e.target.value }))}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 320 },
                  },
                }}
              >
                <MenuItem value="ALL">ALL</MenuItem>
                {workflowGroups.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Assigned Workflow</InputLabel>
              <Select
                label="Assigned Workflow"
                value={filters.assigned_workflow}
                onChange={(e) => setFilters((f) => ({ ...f, assigned_workflow: e.target.value }))}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 320 },
                  },
                }}
              >
                <MenuItem value="">ALL</MenuItem>
                {allWorkflowOptions.map((w) => (
                  <MenuItem key={w} value={w}>
                    {w}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="ALL">ALL</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              sx={{ minWidth: 240 }}
              label="Schedule Name"
              value={filters.schedule_name}
              onChange={(e) => setFilters((f) => ({ ...f, schedule_name: e.target.value }))}
            />
            {hasActiveFilters ? (
              <Button variant="outlined" onClick={onClearFilters}>
                Clear Filters
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Box>

      {errorText ? <Alert severity="error" sx={{ mb: 2 }}>{errorText}</Alert> : null}
      {apiFailed ? <Alert severity="error" sx={{ mb: 2 }}>{apiMessage || "Scheduler API returned non-success state."}</Alert> : null}

      <Box
        sx={{
          border: "1px solid #d5d9e0",
          borderRadius: 2,
          overflowX: "auto",
          bgcolor: "#f6f7fa",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
        }}
      >
        <Box sx={{ minWidth: 1220 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: gridCols,
              px: 1.5,
              py: 1.25,
              fontWeight: 700,
              fontSize: "0.8rem",
              letterSpacing: 0.2,
              textTransform: "uppercase",
              color: "#5f6878",
              borderBottom: "1px solid #d5d9e0",
              bgcolor: "#eceff3",
            }}
          >
            <Box>Action</Box>
            <Box>WF Group Placement</Box>
            <Box>Schedule Name</Box>
            <Box>Workflows</Box>
            <Box>Last Run Time</Box>
            <Box>Remaining</Box>
            <Box>Next Run Time</Box>
            <Box>Run Criteria</Box>
            <Box>Execution History</Box>
          </Box>
        </Box>
        {!isGridLoading ? rows.map((row) => {
          const workflows = toList(row.workflows);
          const isDisabled = String(row.status || "").toLowerCase() === "disabled";
          return (
            <Box
              key={`${row.workflow_group}-${row.schedule_name}`}
              sx={{
                minWidth: 1220,
                display: "grid",
                gridTemplateColumns: gridCols,
                px: 1.5,
                py: 1.1,
                color: "#4b5566",
                borderTop: "1px solid #e1e5ec",
                alignItems: "center",
                bgcolor: isDisabled ? "#fff1f1" : "white",
                "&:nth-of-type(even)": { bgcolor: isDisabled ? "#ffe9e9" : "#f9fafc" },
                "&:hover": { bgcolor: isDisabled ? "#ffe1e1" : "#f0f3f8" },
                opacity: isDisabled ? 0.96 : 1,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <IconButton
                  size="small"
                  onClick={(e) => openActionMenu(e, row)}
                  title="Actions"
                  sx={{ color: "#6a7382", border: "1px solid #d2d8e2", borderRadius: 1, width: 30, height: 30 }}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.workflow_group}</Box>
              <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.schedule_name}</Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                <IconButton
                  size="small"
                  onClick={() => openWorkflowSlider(row)}
                  title="Show assigned workflows"
                  sx={{ color: "#6a7382", border: "1px solid #d2d8e2", borderRadius: 1, width: 26, height: 26, flexShrink: 0 }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box>{toText(row.last_run_time)}</Box>
              <Box>{toText(row.remaining_count, "0")}</Box>
              <Box>{toText(row.next_run_time)}</Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                <IconButton
                  size="small"
                  onClick={() => setViewTarget(row)}
                  title="View run criteria detail"
                  sx={{ color: "#6a7382", border: "1px solid #d2d8e2", borderRadius: 1, width: 26, height: 26, flexShrink: 0 }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => setHistoryTarget(row)}
                  title="Execution History"
                  sx={{ color: "#6a7382", border: "1px solid #d2d8e2", borderRadius: 1 }}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        }) : null}
        {isGridLoading ? (
          <Box sx={{ p: 2.5, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={22} />
          </Box>
        ) : !rows.length ? (
          <Box sx={{ p: 2, color: "#7a8393" }}>No schedules found for selected filters.</Box>
        ) : null}
      </Box>

      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={closeActionMenu}
      >
        <MuiMenuItem
          onClick={() => {
            setEditing(actionMenu.row);
            closeActionMenu();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem
          disabled={changeStatusState.isLoading}
          onClick={() => {
            onStatusToggle(actionMenu.row);
            closeActionMenu();
          }}
        >
          <ListItemIcon>
            {changeStatusState.isLoading ? (
              <CircularProgress size={16} />
            ) : actionMenu.row?.status === "enabled" ? (
              <BlockIcon fontSize="small" />
            ) : (
              <PlayArrowIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {changeStatusState.isLoading
              ? "Updating..."
              : actionMenu.row?.status === "enabled"
                ? "Disable"
                : "Enable"}
          </ListItemText>
        </MuiMenuItem>
        <MuiMenuItem
          onClick={() => {
            setHistoryTarget(actionMenu.row);
            closeActionMenu();
          }}
        >
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Execution History</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem
          onClick={() => {
            setPendingDelete(actionMenu.row);
            closeActionMenu();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MuiMenuItem>
      </Menu>

      <ScheduleEditorFullScreen
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={handleEditorSaved}
        isNew={!!editing?.isNew}
        row={editing?.isNew ? null : editing}
        account={effectiveAccount}
        baseAccount={baseAccount}
        user={user}
        accounts={accounts}
        workflowGroups={workflowGroups}
        assignableWorkflows={assignableWorkflows}
        calendarNames={calendarNames}
        existingSchedules={rows}
      />

      <Drawer anchor="left" open={!!historyTarget} onClose={() => setHistoryTarget(null)}>
        <Box sx={{ width: historyDrawerWidth, maxWidth: "95vw", minWidth: 760, height: "100%", display: "flex", flexDirection: "column", bgcolor: "#f6f7fa", position: "relative" }}>
          <Box
            onMouseDown={startHistoryResize}
            title="Drag to resize"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 10,
              height: "100%",
              cursor: "ew-resize",
              zIndex: 3,
              "&:hover": { bgcolor: "rgba(95,104,120,0.08)" },
            }}
          />
          <Box sx={{ bgcolor: "#eceff3", borderBottom: "1px solid #d5d9e0", px: 1.25, py: 0.9, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#5f6878", textTransform: "uppercase", letterSpacing: 0.2 }}>
              Execution History
            </Typography>
            <IconButton size="small" onClick={() => setHistoryTarget(null)} sx={{ color: "#6a7382", border: "1px solid #d2d8e2", borderRadius: 1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ px: 2, py: 0.75, borderBottom: "1px solid #d5d9e0", bgcolor: "#f8f9fc", color: "#4b5566" }}>
            <Typography variant="body2"><b>Account Name:</b> {effectiveAccount || "-"}</Typography>
            <Typography variant="body2"><b>Schedule Name:</b> {historyTarget?.schedule_name || "-"}</Typography>
            <Typography variant="body2"><b>Workflow Group:</b> {historyTarget?.workflow_group || "-"}</Typography>
          </Box>
          <Box sx={{ px: 1, py: 1, overflow: "auto" }}>
            <Box sx={{ minWidth: 980 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "50px 220px 150px 160px 1fr 120px",
                  bgcolor: "#eceff3",
                  color: "#5f6878",
                  fontWeight: 700,
                  border: "1px solid #d5d9e0",
                  textTransform: "uppercase",
                  fontSize: "0.78rem",
                }}
              >
                <Box sx={{ px: 1, py: 0.75 }}>#</Box>
                <Box sx={{ px: 1, py: 0.75 }}>DATE</Box>
                <Box sx={{ px: 1, py: 0.75 }}>Workflow Name</Box>
                <Box sx={{ px: 1, py: 0.75 }}>Recurrence</Box>
                <Box sx={{ px: 1, py: 0.75 }}>Status</Box>
                <Box sx={{ px: 1, py: 0.75 }}>Transactions</Box>
              </Box>
              {historyRows.map((h, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "50px 220px 150px 160px 1fr 120px",
                    borderLeft: "1px solid #e1e5ec",
                    borderRight: "1px solid #e1e5ec",
                    borderBottom: "1px solid #e1e5ec",
                    bgcolor: idx % 2 === 0 ? "#fff" : "#f9fafc",
                    "&:hover": { bgcolor: "#f0f3f8" },
                  }}
                >
                  <Box sx={{ px: 1, py: 0.75, fontSize: "0.82rem" }}>{idx + 1}</Box>
                  <Box sx={{ px: 1, py: 0.75, whiteSpace: "nowrap", fontSize: "0.82rem" }}>{formatHistoryDate(h.dateTimestamp || h.date)}</Box>
                  <Box sx={{ px: 1, py: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "0.82rem" }}>{toText(h.workflowName || h.workflow_name)}</Box>
                  <Box sx={{ px: 1, py: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "0.82rem" }}>{formatHistoryRecurrence(h.recurrence || h.scheduleInterval)}</Box>
                  <Box sx={{ px: 1, py: 0.75, wordBreak: "break-word", lineHeight: 1.3, fontSize: "0.82rem" }}>{formatHistoryStatus(h.status || h.statusDetails)}</Box>
                  <Box sx={{ px: 1, py: 0.75, textAlign: "right", fontSize: "0.82rem" }}>{toText(h.transactions || h.transactionCount)}</Box>
                </Box>
              ))}
              {!historyRows.length ? (
                <Box sx={{ p: 2, border: "1px solid #d0d0d0", borderTop: 0, bgcolor: "#fff", color: "text.secondary" }}>
                  No execution history rows found.
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Drawer anchor="left" open={!!workflowTarget} onClose={() => setWorkflowTarget(null)}>
        <Box sx={{ width: 460, maxWidth: "90vw", height: "100%", display: "flex", flexDirection: "column", bgcolor: "#f6f7fa" }}>
          <Box sx={{ bgcolor: "#eceff3", borderBottom: "1px solid #d5d9e0", px: 1.25, py: 0.9, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#5f6878", textTransform: "uppercase" }}>
              Assigned Workflows
            </Typography>
            <IconButton size="small" onClick={() => setWorkflowTarget(null)} sx={{ color: "#6a7382", border: "1px solid #d2d8e2", borderRadius: 1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 1.5, borderBottom: "1px solid #d5d9e0", bgcolor: "#f8f9fc" }}>
            <Typography variant="body2"><b>Schedule:</b> {workflowTarget?.schedule_name || "-"}</Typography>
            <Typography variant="body2"><b>Workflow Group:</b> {workflowTarget?.workflow_group || "-"}</Typography>
          </Box>
          <Box sx={{ p: 1.5, overflow: "auto" }}>
            {workflowLoading ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={22} />
              </Box>
            ) : Object.keys(groupAssignedWorkflows(workflowItems)).length ? (
              Object.entries(groupAssignedWorkflows(workflowItems)).map(([group, names]) => (
                <Box key={group} sx={{ px: 1, py: 0.85, border: "1px solid #e1e5ec", borderRadius: 1, bgcolor: "#fff", mb: 1.1 }}>
                  <Typography variant="body2" sx={{ color: "#4b5566", fontWeight: 700 }}>• {group}</Typography>
                  <Box sx={{ pl: 2, pt: 0.35 }}>
                    {names.map((name) => (
                      <Typography key={`${group}/${name}`} variant="body2" sx={{ color: "#4b5566" }}>
                        ◦ {name}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: "#7a8393" }}>No assigned workflows.</Typography>
            )}
          </Box>
        </Box>
      </Drawer>

      <Drawer anchor="right" open={!!viewTarget} onClose={() => setViewTarget(null)}>
        <Box sx={{ width: 520, maxWidth: "95vw", height: "100%", display: "flex", flexDirection: "column", bgcolor: "#f6f7fa" }}>
          <Box sx={{ bgcolor: "#eceff3", borderBottom: "1px solid #d5d9e0", px: 1.25, py: 0.9, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#5f6878", textTransform: "uppercase" }}>
              Schedule Detail
            </Typography>
            <IconButton size="small" onClick={() => setViewTarget(null)} sx={{ color: "#6a7382", border: "1px solid #d2d8e2", borderRadius: 1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, overflow: "auto" }}>
            {(() => {
              const detail = viewTarget ? rowScheduleDetails[`${viewTarget.workflow_group}::${viewTarget.schedule_name}`] || {} : {};
              const rowsToShow = [
                ["Schedule Name", viewTarget?.schedule_name || "-"],
                ["Workflow Group", viewTarget?.workflow_group || "-"],
                ["Status", toText(detail.ENABLED === true ? "enabled" : detail.ENABLED === false ? "disabled" : viewTarget?.status)],
                ["Run On Server", toDisplayLabel(detail.RUN_ON_SERVER)],
                ["Start Date", toText(detail.START_DATE)],
                ["End Date", toText(detail.END_DATE)],
                ["From Time", toText(detail.TIME)],
                ["To Time", toText(detail.END_TIME)],
                ["Run Time", toText(detail.RUN_TIME)],
                ["Interval", `${toText(detail.INTERVAL_QTY)} ${toText(detail.INTERVAL_UNIT)}`],
                ["Misfire", toDisplayLabel(detail.MISFIRE_INSTRUCTION)],
                ["Priority", toText(detail.PRIORITY)],
              ];
              return rowsToShow.map(([k, v]) => (
                <Box key={k} sx={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 1, py: 0.8, borderBottom: "1px solid #e1e5ec" }}>
                  <Typography variant="body2" sx={{ color: "#6a7382", fontWeight: 700 }}>{k}</Typography>
                  <Typography variant="body2" sx={{ color: "#4b5566", wordBreak: "break-word" }}>{v || "-"}</Typography>
                </Box>
              ));
            })()}
          </Box>
        </Box>
      </Drawer>

      <Dialog open={!!pendingDelete} onClose={deleteState.isLoading ? undefined : () => setPendingDelete(null)}>
        <DialogTitle>Delete schedule?</DialogTitle>
        <DialogContent>
          <Typography>
            Confirm delete of <b>{pendingDelete?.schedule_name}</b>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleteState.isLoading}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={onDelete}
            disabled={deleteState.isLoading}
            startIcon={deleteState.isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleteState.isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
